// Pagos en línea por Banchile Pagos (Web Checkout), compartidos por el panel
// (la mensualidad) y Chatbularis (las cargas de saldo).
//
// Tres piezas, las que pide Banchile para certificar y las que el estudio
// necesita para reconocer un cobro:
//   · `usePagoVuelta` + `ResumenPago`: al volver del checkout (?pago=<id>), el
//     resumen con referencia, estado, motivo, fecha y hora, monto en CLP,
//     autorización y contacto. Pregunta hasta que el pago deje de estar
//     pendiente.
//   · `HistorialPagos`: todos los pagos en línea del estudio, del más reciente
//     al más antiguo.
// Van como tarjetas blancas para leerse igual sobre el panel (oscuro) que
// sobre Chatbularis (claro).
import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';

export type Pago = {
  /** Nuestra referencia del pago (la que ve Banchile). */
  id: string;
  concepto: 'saldo_ia' | 'suscripcion';
  estado: 'pendiente' | 'pagada' | 'rechazada' | 'anulada';
  montoClp: number;
  bonoClp: number;
  meses: number | null;
  ciclo: string | null;
  creado: string;
  pagada: string | null;
  resuelta: string | null;
  /** El número de la sesión en Banchile ("Pago en Banchile Pagos No"). */
  requestId: number | null;
  /** El mensaje de Banchile sobre el resultado. */
  motivo: string | null;
  medio: string | null;
  autorizacion: string | null;
};

export type EstadoPago = Pago & { saldo: { saldoClp: number } | null; periodoFin: string | null };

/** Cargar saldo de Chatbularis: devuelve adónde mandar el navegador. */
export const pagarSaldoIa = (montoClp: number) =>
  api<{ id: string; url: string }>('/pagos/saldo-ia', { method: 'POST', body: { montoClp } });

/** Pagar la mensualidad del panel: el monto lo pone el servidor. */
/** `token`: la sesión corta que el login le da al admin de un estudio vencido,
 *  que no alcanza a entrar al panel pero sí puede pagar desde el bloqueo. */
export const pagarSuscripcion = (token?: string) =>
  api<{ id: string; url: string }>('/pagos/suscripcion', { method: 'POST', body: {}, token });

export const estadoPago = (id: string) => api<EstadoPago>(`/pagos/${encodeURIComponent(id)}`);
export const historialPagos = () => api<Pago[]>('/pagos');

const clp = (n: number) => '$' + Math.round(n).toLocaleString('es-CL');
const fechaHora = (iso: string) =>
  new Date(iso).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Santiago' });
const fechaLarga = (iso: string) =>
  new Date(iso).toLocaleDateString('es-CL', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'America/Santiago' });

const NOMBRE_ESTADO: Record<string, string> = { pagada: 'Aprobado', rechazada: 'Rechazado', anulada: 'Anulado', pendiente: 'Pendiente' };
const COLOR_ESTADO: Record<string, [string, string]> = {
  pagada: ['#EAF6EE', '#1B5E20'],
  rechazada: ['#FDEDED', '#8A2A2A'],
  anulada: ['#F1F3F6', '#4B5563'],
  pendiente: ['#FFF6E5', '#8A5A00'],
};
const concepto = (p: Pago) =>
  p.concepto === 'suscripcion'
    ? `Tabularis, ${p.meses === 1 || !p.meses ? '1 mes' : `${p.meses} meses`}`
    : `Saldo Chatbularis${p.bonoClp ? ` (+${clp(p.bonoClp)} de regalo)` : ''}`;

/** La vuelta de Banchile: lee ?pago= de la dirección, la limpia y pregunta
 *  cada dos segundos (hasta un minuto) cómo quedó. `listo` en false mientras
 *  no se pueda preguntar (por ejemplo, sin sesión). */
export function usePagoVuelta(listo: boolean, alAprobarse?: (p: EstadoPago) => void) {
  const [vuelta, setVuelta] = useState<null | { id: string; estado: string; pago?: EstadoPago }>(() => {
    try {
      const id = new URLSearchParams(window.location.search).get('pago');
      return id ? { id, estado: 'pendiente' } : null;
    } catch { return null; }
  });
  useEffect(() => {
    if (!vuelta || vuelta.estado !== 'pendiente' || !listo) return;
    window.history.replaceState(null, '', window.location.pathname);
    let vivo = true;
    let intentos = 0;
    const preguntar = async () => {
      if (!vivo) return;
      intentos++;
      try {
        const p = await estadoPago(vuelta.id);
        if (!vivo) return;
        setVuelta({ id: p.id, estado: p.estado, pago: p });
        if (p.estado !== 'pendiente') {
          if (p.estado === 'pagada') alAprobarse?.(p);
          return;
        }
      } catch { /* se reintenta */ }
      if (intentos >= 30) { setVuelta((v) => (v ? { ...v, estado: 'demora' } : v)); return; }
      setTimeout(preguntar, 2000);
    };
    void preguntar();
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vuelta?.id, listo]);
  return [vuelta, () => setVuelta(null)] as const;
}

const caja: React.CSSProperties = {
  position: 'relative', background: '#fff', color: '#0D1B35', border: '1px solid #DDE6F3', borderRadius: 12,
  padding: '14px 40px 12px 16px', fontFamily: 'Inter, sans-serif', fontSize: 13.5, lineHeight: 1.5, textAlign: 'left',
};

/** El resumen de un pago al volver de Banchile Pagos. */
export const ResumenPago: React.FC<{ v: { id: string; estado: string; pago?: EstadoPago }; onCerrar: () => void }> = ({ v, onCerrar }) => {
  const p = v.pago;
  const esSaldo = p?.concepto === 'saldo_ia';
  const titulo =
    v.estado === 'pagada'
      ? esSaldo
        ? `Listo: cargaste ${clp(p!.montoClp)}${p!.bonoClp ? ` y te regalamos ${clp(p!.bonoClp)}` : ''}. Tu saldo quedó en ${clp(p!.saldo?.saldoClp ?? 0)}.`
        : `Listo: pagaste ${clp(p?.montoClp ?? 0)} y tu estudio quedó activo${p?.periodoFin ? ` hasta el ${fechaLarga(p.periodoFin)}` : ''}.`
      : v.estado === 'pendiente'
        ? 'Confirmando tu pago con Banchile Pagos…'
        : v.estado === 'demora'
          ? 'Banchile Pagos todavía no confirma el pago. Si lo completaste, se va a acreditar solo en unos minutos; si no, no se cobró nada.'
          : 'El pago no se completó y no se cobró nada. Puedes intentarlo de nuevo.';
  const [fondo, texto] = COLOR_ESTADO[v.estado] ?? COLOR_ESTADO.pendiente;
  const cuando = p?.resuelta ?? p?.creado;
  return (
    <div style={{ ...caja, borderLeft: `4px solid ${texto}` }} role="status">
      <div style={{ fontWeight: 600, marginBottom: p ? 8 : 0 }}>{titulo}</div>
      {p && (
        <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '2px 14px', margin: '0 0 6px', fontSize: 12.5 }}>
          <dt style={{ color: '#6B82A0' }}>Concepto</dt><dd style={{ margin: 0 }}>{concepto(p)}</dd>
          <dt style={{ color: '#6B82A0' }}>Estado</dt>
          <dd style={{ margin: 0 }}><span style={{ background: fondo, color: texto, borderRadius: 99, padding: '1px 8px', fontWeight: 600, fontSize: 11.5 }}>{NOMBRE_ESTADO[p.estado] ?? p.estado}</span></dd>
          {p.motivo && <><dt style={{ color: '#6B82A0' }}>Motivo</dt><dd style={{ margin: 0 }}>{p.motivo}</dd></>}
          <dt style={{ color: '#6B82A0' }}>Total</dt><dd style={{ margin: 0 }}>{clp(p.montoClp)} CLP</dd>
          {cuando && <><dt style={{ color: '#6B82A0' }}>Fecha y hora</dt><dd style={{ margin: 0 }}>{fechaHora(cuando)}</dd></>}
          <dt style={{ color: '#6B82A0' }}>Referencia</dt><dd style={{ margin: 0, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11.5, overflowWrap: 'anywhere' }}>{p.id}</dd>
          {p.requestId != null && <><dt style={{ color: '#6B82A0' }}>N° Banchile Pagos</dt><dd style={{ margin: 0 }}>{p.requestId}</dd></>}
          {p.estado === 'pagada' && p.autorizacion && <><dt style={{ color: '#6B82A0' }}>Autorización</dt><dd style={{ margin: 0 }}>{p.autorizacion}</dd></>}
          {p.estado === 'pagada' && p.medio && <><dt style={{ color: '#6B82A0' }}>Medio de pago</dt><dd style={{ margin: 0 }}>{p.medio}</dd></>}
        </dl>
      )}
      <div style={{ fontSize: 12, color: '#6B82A0' }}>¿Dudas con este pago? Escríbenos a contacto@tabularis.cl con la referencia.</div>
      {v.estado !== 'pendiente' && (
        <button onClick={onCerrar} title="Cerrar" aria-label="Cerrar"
          style={{ position: 'absolute', top: 6, right: 8, background: 'none', border: 'none', fontSize: 18, color: '#6B82A0', cursor: 'pointer', lineHeight: 1 }}>×</button>
      )}
    </div>
  );
};

/** Los pagos en línea del estudio, del más reciente al más antiguo. */
export const HistorialPagos: React.FC<{ titulo?: string }> = ({ titulo = 'Historial de pagos en línea' }) => {
  const [pagos, setPagos] = useState<Pago[] | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => { historialPagos().then(setPagos).catch(() => setError(true)); }, []);
  if (error) return null;
  return (
    <div style={{ ...caja, padding: '12px 16px' }}>
      <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#6B82A0', marginBottom: 6 }}>{titulo}</div>
      {pagos === null ? (
        <div style={{ fontSize: 13, color: '#6B82A0' }}>Cargando…</div>
      ) : pagos.length === 0 ? (
        <div style={{ fontSize: 13, color: '#6B82A0' }}>Todavía no hay pagos en línea.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ color: '#6B82A0', textAlign: 'left' }}>
                <th style={{ padding: '4px 8px 4px 0', fontWeight: 600 }}>Fecha y hora</th>
                <th style={{ padding: '4px 8px', fontWeight: 600 }}>Concepto</th>
                <th style={{ padding: '4px 8px', fontWeight: 600 }}>Estado</th>
                <th style={{ padding: '4px 8px', fontWeight: 600, textAlign: 'right' }}>Valor</th>
                <th style={{ padding: '4px 8px', fontWeight: 600 }}>Autorización</th>
                <th style={{ padding: '4px 0 4px 8px', fontWeight: 600 }}>Referencia</th>
              </tr>
            </thead>
            <tbody>
              {pagos.map((p) => {
                const [fondo, texto] = COLOR_ESTADO[p.estado] ?? COLOR_ESTADO.pendiente;
                return (
                  <tr key={p.id} style={{ borderTop: '1px solid #EEF2F8' }}>
                    <td style={{ padding: '6px 8px 6px 0', whiteSpace: 'nowrap' }}>{fechaHora(p.creado)}</td>
                    <td style={{ padding: '6px 8px' }}>{concepto(p)}</td>
                    <td style={{ padding: '6px 8px' }}>
                      <span title={p.motivo ?? undefined} style={{ background: fondo, color: texto, borderRadius: 99, padding: '1px 8px', fontWeight: 600, fontSize: 11 }}>{NOMBRE_ESTADO[p.estado] ?? p.estado}</span>
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{clp(p.montoClp)} CLP</td>
                    <td style={{ padding: '6px 8px' }}>{p.estado === 'pagada' ? p.autorizacion ?? '—' : '—'}</td>
                    <td style={{ padding: '6px 0 6px 8px', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11 }}>{p.requestId ?? p.id}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
